// Create / edit form for a hub, shown in a modal. Used by HubList.
//
// One component handles both "new" and "edit": if `hub` is provided we update,
// otherwise we create. The parent passes the existing hubs so we can warn about
// duplicate codes instantly; the database also enforces uniqueness as the real
// guarantee (we catch that error too).

import { useState } from "react";
import { Button, Field, Input, Modal } from "../../components/ui";
import type { Hub, HubInput } from "../../lib/types";
import { isUniqueViolation, getErrorMessage } from "../../lib/errors";
import {
  parseOptionalNumber,
  isValidEmail,
  isValidLatitude,
  isValidLongitude,
} from "../../lib/validation";
import { createHub, updateHub } from "./hubsApi";

interface Props {
  accountId: string;
  hub: Hub | null; // null = creating a new hub
  existingHubs: Hub[]; // used to detect duplicate codes before hitting the DB
  onClose: () => void;
  onSaved: () => void;
}

export default function HubForm({ accountId, hub, existingHubs, onClose, onSaved }: Props) {
  const [name, setName] = useState(hub?.name ?? "");
  const [code, setCode] = useState(hub?.code ?? "");
  const [city, setCity] = useState(hub?.city ?? "");
  const [state, setState] = useState(hub?.state ?? "");
  const [region, setRegion] = useState(hub?.region ?? "");
  const [manager, setManager] = useState(hub?.manager ?? "");
  const [managerEmail, setManagerEmail] = useState(hub?.managerEmail ?? "");
  const [regionalManager, setRegionalManager] = useState(hub?.regionalManager ?? "");
  const [lat, setLat] = useState(hub?.lat != null ? String(hub.lat) : "");
  const [lng, setLng] = useState(hub?.lng != null ? String(hub.lng) : "");
  const [mhePolybag, setMhePolybag] = useState(hub?.mhePolybag != null ? String(hub.mhePolybag) : "");
  const [mheCartons, setMheCartons] = useState(hub?.mheCartons != null ? String(hub.mheCartons) : "");
  const [mheIrregular, setMheIrregular] = useState(hub?.mheIrregular != null ? String(hub.mheIrregular) : "");
  const [active, setActive] = useState(hub?.active ?? true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /** Is this code already used by a DIFFERENT hub in the account? (case-insensitive) */
  function codeIsDuplicate(value: string): boolean {
    const c = value.trim().toLowerCase();
    if (!c) return false; // blank codes are allowed and never collide
    return existingHubs.some((h) => h.id !== hub?.id && (h.code ?? "").trim().toLowerCase() === c);
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim()) {
      setError("Name is required.");
      return;
    }
    if (codeIsDuplicate(code)) {
      setError(`Code "${code.trim()}" is already used by another hub. Codes must be unique.`);
      return;
    }

    // Validate + normalize numeric / email fields before they can reach Supabase.
    const latP = parseOptionalNumber(lat);
    const lngP = parseOptionalNumber(lng);
    if (!latP.ok || (latP.value !== null && !isValidLatitude(latP.value))) {
      setError("Latitude must be a number between -90 and 90.");
      return;
    }
    if (!lngP.ok || (lngP.value !== null && !isValidLongitude(lngP.value))) {
      setError("Longitude must be a number between -180 and 180.");
      return;
    }
    // MHE sortation capacity per package type (all optional, non-negative).
    const mhePolyP = parseOptionalNumber(mhePolybag);
    const mheCartP = parseOptionalNumber(mheCartons);
    const mheIrrP = parseOptionalNumber(mheIrregular);
    if (!mhePolyP.ok || (mhePolyP.value !== null && mhePolyP.value < 0)) {
      setError("Polybag MHE capacity must be a positive number.");
      return;
    }
    if (!mheCartP.ok || (mheCartP.value !== null && mheCartP.value < 0)) {
      setError("Cartons MHE capacity must be a positive number.");
      return;
    }
    if (!mheIrrP.ok || (mheIrrP.value !== null && mheIrrP.value < 0)) {
      setError("Irregular MHE capacity must be a positive number.");
      return;
    }
    if (managerEmail.trim() && !isValidEmail(managerEmail)) {
      setError("Please enter a valid hub manager email.");
      return;
    }

    // The hub's daily capacity IS the sum of its MHE sortation capacities
    // (Polybag + Cartons + Irregular). Null only when no MHE value is set.
    const mheValues = [mhePolyP.value, mheCartP.value, mheIrrP.value].filter(
      (v): v is number => v !== null
    );
    const mheTotal = mheValues.length === 0 ? null : Math.round(mheValues.reduce((s, v) => s + v, 0));

    setBusy(true);
    setError(null);
    const input: HubInput = {
      name,
      code,
      city,
      state,
      region,
      manager,
      managerEmail,
      regionalManager,
      lat: latP.value,
      lng: lngP.value,
      capacity: mheTotal,
      mhePolybag: mhePolyP.value === null ? null : Math.round(mhePolyP.value),
      mheCartons: mheCartP.value === null ? null : Math.round(mheCartP.value),
      mheIrregular: mheIrrP.value === null ? null : Math.round(mheIrrP.value),
      active,
    };
    try {
      if (hub) {
        await updateHub(accountId, hub.id, input);
      } else {
        await createHub(accountId, input);
      }
      onSaved();
    } catch (err: unknown) {
      // The DB unique index is the real guard; turn it into a friendly message.
      if (isUniqueViolation(err)) {
        setError(`Code "${code.trim()}" is already used by another hub. Codes must be unique.`);
      } else {
        setError(getErrorMessage(err, "Failed to save hub."));
      }
    } finally {
      setBusy(false);
    }
  }

  // Live preview of the derived daily capacity (sum of the MHE inputs).
  const mhePreview = (() => {
    const nums = [mhePolybag, mheCartons, mheIrregular]
      .map((s) => {
        const n = Number(s);
        return s.trim() === "" || Number.isNaN(n) ? null : n;
      })
      .filter((v): v is number => v !== null);
    return nums.length === 0 ? null : nums.reduce((s, v) => s + v, 0);
  })();

  return (
    <Modal title={hub ? "Edit hub" : "New hub"} onClose={onClose}>
      <form onSubmit={submit} className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <Field label="Name *">
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Atlanta" autoFocus />
          </Field>
          <Field label="Code">
            <Input value={code} onChange={(e) => setCode(e.target.value)} placeholder="ATL" />
          </Field>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Field label="City">
            <Input value={city} onChange={(e) => setCity(e.target.value)} placeholder="Atlanta" />
          </Field>
          <Field label="State">
            <Input value={state} onChange={(e) => setState(e.target.value)} placeholder="GA" />
          </Field>
        </div>

        <Field label="Region">
          <Input value={region} onChange={(e) => setRegion(e.target.value)} placeholder="Southeast" />
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Hub manager">
            <Input value={manager} onChange={(e) => setManager(e.target.value)} placeholder="Dana Lee" />
          </Field>
          <Field label="Hub manager email">
            <Input
              type="email"
              value={managerEmail}
              onChange={(e) => setManagerEmail(e.target.value)}
              placeholder="dana@example.com"
            />
          </Field>
        </div>

        <Field label="Regional manager">
          <Input
            value={regionalManager}
            onChange={(e) => setRegionalManager(e.target.value)}
            placeholder="Sam Cruz"
          />
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Latitude">
            <Input
              type="number"
              step="any"
              value={lat}
              onChange={(e) => setLat(e.target.value)}
              placeholder="33.749"
            />
          </Field>
          <Field label="Longitude">
            <Input
              type="number"
              step="any"
              value={lng}
              onChange={(e) => setLng(e.target.value)}
              placeholder="-84.388"
            />
          </Field>
        </div>

        <div>
          <div className="mb-1.5 flex items-baseline justify-between">
            <p className="text-xs font-medium text-slate-500">
              MHE sortation capacity by package type (packages/day)
            </p>
            <p className="text-xs text-slate-500">
              Daily capacity:{" "}
              <span className="font-semibold text-slate-800">
                {mhePreview === null ? "—" : mhePreview.toLocaleString()}
              </span>
            </p>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <Field label="Polybag">
              <Input
                type="number"
                value={mhePolybag}
                onChange={(e) => setMhePolybag(e.target.value)}
                placeholder="20000"
                min={0}
              />
            </Field>
            <Field label="Cartons">
              <Input
                type="number"
                value={mheCartons}
                onChange={(e) => setMheCartons(e.target.value)}
                placeholder="25000"
                min={0}
              />
            </Field>
            <Field label="Irregular">
              <Input
                type="number"
                value={mheIrregular}
                onChange={(e) => setMheIrregular(e.target.value)}
                placeholder="5000"
                min={0}
              />
            </Field>
          </div>
        </div>

        <label className="flex items-center gap-2 text-sm text-slate-700">
          <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} />
          Active
        </label>

        {error && <p className="text-sm text-red-600">{error}</p>}

        <div className="flex justify-end gap-2 pt-1">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={busy}>
            {busy ? "Saving…" : hub ? "Save changes" : "Create hub"}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
